/**
 * B1 — Alertas e Incidentes Tab
 * B1.1: Configurar alertas (CPU>80%, pod restart>3x, disco>90%, OOM)
 * B1.2: Canais notificacao (Telegram, email)
 * B1.3: Thresholds editaveis por alerta
 * B1.4: Historico de incidents (timeline firing/resolved)
 * B1.5: Silenciar (mute) + dashboard saude (verde/amarelo/vermelho)
 */
import { T } from '../../styles/tokens';
import { useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { SkeletonBar, Button } from '../../components/ui';

const colors = {
  bg: T.bg, card: T.bgCard, cardBorder: T.bgHover,
  text: T.border, textMuted: T.textSecondary,
  green: T.success, red: T.danger, yellow: T.warning, blue: T.primary, purple: T.purple,
};

const alertRules = signal<any[]>([]);
const incidents = signal<any[]>([]);
const alertsLoading = signal(false);
const alertsError = signal('');
const alertSection = signal<'saude' | 'regras' | 'incidentes'>('saude');
const editingRule = signal<string | null>(null);

// Default alert rule templates
const DEFAULT_RULES = [
  { id: 'cpu-high', name: 'CPU Alta', metric: 'cpu', operator: '>', threshold: 80, severity: 'warning', description: 'CPU acima de 80%', enabled: true, muted: false, channels: ['telegram'] },
  { id: 'memory-high', name: 'Memoria Alta', metric: 'memory', operator: '>', threshold: 90, severity: 'critical', description: 'Memoria acima de 90%', enabled: true, muted: false, channels: ['telegram'] },
  { id: 'disk-full', name: 'Disco Cheio', metric: 'disk', operator: '>', threshold: 90, severity: 'critical', description: 'Disco acima de 90%', enabled: true, muted: false, channels: ['telegram'] },
  { id: 'pod-restart', name: 'Pod Restarts', metric: 'restarts', operator: '>', threshold: 3, severity: 'warning', description: 'Pod com mais de 3 restarts', enabled: true, muted: false, channels: ['telegram'] },
  { id: 'oom-killed', name: 'OOM Killed', metric: 'oom', operator: '=', threshold: 1, severity: 'critical', description: 'Pod morto por falta de memoria (OOMKilled)', enabled: true, muted: false, channels: ['telegram'] },
  { id: 'pod-crashloop', name: 'CrashLoopBackOff', metric: 'crashloop', operator: '=', threshold: 1, severity: 'critical', description: 'Pod em CrashLoopBackOff', enabled: true, muted: false, channels: ['telegram'] },
];

async function fetchAlerts() {
  alertsLoading.value = true;
  try {
    const data = await api.get<any>('/admin/cluster/metrics/alerts');
    alertRules.value = data.rules || DEFAULT_RULES;
    incidents.value = data.incidents || [];
    alertsError.value = '';
  } catch (e: unknown) {
    alertsError.value = e instanceof Error ? e.message : 'Erro ao carregar alertas';
    alertRules.value = DEFAULT_RULES;
    incidents.value = [];
  }
  alertsLoading.value = false;
}

async function saveRule(rule: any) {
  try {
    await api.post('/admin/cluster/metrics/alerts/rules', rule);
    editingRule.value = null;
    await fetchAlerts();
  } catch (e: unknown) {
    alert('Erro ao salvar: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

async function toggleMute(ruleId: string) {
  const rule = alertRules.value.find(r => r.id === ruleId);
  if (!rule) return;
  rule.muted = !rule.muted;
  alertRules.value = [...alertRules.value];
  try { await api.post('/admin/cluster/metrics/alerts/rules', rule); } catch { /* silent */ }
}

async function toggleEnabled(ruleId: string) {
  const rule = alertRules.value.find(r => r.id === ruleId);
  if (!rule) return;
  rule.enabled = !rule.enabled;
  alertRules.value = [...alertRules.value];
  try { await api.post('/admin/cluster/metrics/alerts/rules', rule); } catch { /* silent */ }
}

function getSeverityColor(severity: string) {
  if (severity === 'critical') return colors.red;
  if (severity === 'warning') return colors.yellow;
  return colors.blue;
}

function getHealthStatus(rules: any[], incidents: any[]) {
  const activeIncidents = incidents.filter((i: any) => i.status === 'firing');
  const criticalCount = activeIncidents.filter((i: any) => i.severity === 'critical').length;
  const warningCount = activeIncidents.filter((i: any) => i.severity === 'warning').length;
  if (criticalCount > 0) return { label: 'Critico', color: colors.red, bg: T.dangerBg };
  if (warningCount > 0) return { label: 'Atencao', color: colors.yellow, bg: T.warningBg };
  return { label: 'Saudavel', color: colors.green, bg: T.successBg };
}

export function AlertsTab() {
  useEffect(() => { fetchAlerts(); }, []);

  if (alertsLoading.value && alertRules.value.length === 0) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  const rules = alertRules.value;
  const health = getHealthStatus(rules, incidents.value);
  const activeIncidents = incidents.value.filter((i: any) => i.status === 'firing');
  const resolvedIncidents = incidents.value.filter((i: any) => i.status === 'resolved');

  return (
    <div>
      {alertsError.value && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: colors.red, marginBottom: 16, fontSize: 13 }}>
          {alertsError.value}
        </div>
      )}

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {([
          { key: 'saude' as const, label: 'Dashboard Saude' },
          { key: 'regras' as const, label: 'Regras de Alerta' },
          { key: 'incidentes' as const, label: 'Historico de Incidentes' },
        ]).map(s => (
          <Button variant="ghost" key={s.key} onClick={() => alertSection.value = s.key} style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: alertSection.value === s.key ? colors.blue : colors.card,
            color: T.text, border: `1px solid ${colors.cardBorder}`,
          }}>{s.label}</Button>
        ))}
      </div>

      {/* B1.5: Dashboard Saude */}
      {alertSection.value === 'saude' && (
        <div>
          {/* Health status banner */}
          <div style={{
            background: health.bg, border: `2px solid ${health.color}`, borderRadius: 16,
            padding: 32, textAlign: 'center', marginBottom: 24,
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: `${health.color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', border: `3px solid ${health.color}`,
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={health.color} strokeWidth="2">
                {health.label === 'Saudavel' ? (
                  <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
                ) : health.label === 'Atencao' ? (
                  <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>
                ) : (
                  <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
                )}
              </svg>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: health.color, marginBottom: 4 }}>{health.label}</div>
            <div style={{ fontSize: 14, color: colors.textMuted }}>
              {activeIncidents.length} incidente(s) ativo(s) · {rules.filter(r => r.enabled).length} regras ativas · {rules.filter(r => r.muted).length} silenciadas
            </div>
          </div>

          {/* Quick status grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
            {rules.map(rule => {
              const incident = incidents.value.find((i: any) => i.ruleId === rule.id && i.status === 'firing');
              const statusColor = !rule.enabled ? colors.textMuted : rule.muted ? T.textMuted : incident ? getSeverityColor(rule.severity) : colors.green;
              const statusLabel = !rule.enabled ? 'Desativado' : rule.muted ? 'Silenciado' : incident ? 'FIRING' : 'OK';
              return (
                <div key={rule.id} style={{
                  background: colors.card, borderRadius: 10, padding: 16,
                  border: `1px solid ${statusColor}40`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: colors.text, fontWeight: 600, fontSize: 13 }}>{rule.name}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                      background: `${statusColor}25`, color: statusColor,
                    }}>{statusLabel}</span>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{rule.description}</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                    {rule.metric} {rule.operator} {rule.threshold}{rule.metric === 'cpu' || rule.metric === 'memory' || rule.metric === 'disk' ? '%' : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* B1.1 + B1.3: Regras de Alerta com thresholds editaveis */}
      {alertSection.value === 'regras' && (
        <div>
          <div style={{ display: 'grid', gap: 12 }}>
            {rules.map(rule => {
              const isEditing = editingRule.value === rule.id;
              return (
                <div key={rule.id} style={{
                  background: colors.card, borderRadius: 10, padding: 16,
                  border: `1px solid ${colors.cardBorder}`, opacity: rule.enabled ? 1 : 0.5,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{rule.name}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                        background: `${getSeverityColor(rule.severity)}25`, color: getSeverityColor(rule.severity),
                      }}>{rule.severity.toUpperCase()}</span>
                      {rule.muted && (
                        <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: T.borderHover, color: T.textMuted }}>MUTE</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button variant="ghost" onClick={() => toggleEnabled(rule.id)} style={btnS(rule.enabled ? colors.green : T.textMuted)}>{rule.enabled ? 'Ativo' : 'Inativo'}</Button>
                      <Button variant="ghost" onClick={() => toggleMute(rule.id)} style={btnS(rule.muted ? colors.yellow : T.bgSecondary)}>{rule.muted ? 'Desmute' : 'Mute'}</Button>
                      <Button variant="ghost" onClick={() => editingRule.value = isEditing ? null : rule.id} style={btnS(colors.blue)}>{isEditing ? 'Fechar' : 'Editar'}</Button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{rule.description}</div>
                  <div style={{ fontSize: 12, color: colors.text }}>
                    Condicao: <code style={{ background: T.bgHover, padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{rule.metric} {rule.operator} {rule.threshold}</code>
                    {' · '}Canais: {(rule.channels || []).join(', ') || 'nenhum'}
                  </div>

                  {/* B1.3: Editable thresholds */}
                  {isEditing && (
                    <div style={{ marginTop: 12, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${colors.cardBorder}` }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ color: colors.textMuted, fontSize: 11, display: 'block', marginBottom: 2 }}>Metrica</label>
                          <select id={`rule-metric-${rule.id}`} defaultValue={rule.metric} style={inputS()}>
                            <option value="cpu">CPU</option>
                            <option value="memory">Memoria</option>
                            <option value="disk">Disco</option>
                            <option value="restarts">Restarts</option>
                            <option value="oom">OOM</option>
                            <option value="crashloop">CrashLoop</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ color: colors.textMuted, fontSize: 11, display: 'block', marginBottom: 2 }}>Operador</label>
                          <select id={`rule-op-${rule.id}`} defaultValue={rule.operator} style={inputS()}>
                            <option value=">">&gt;</option>
                            <option value=">=">&gt;=</option>
                            <option value="=">=</option>
                            <option value="<">&lt;</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ color: colors.textMuted, fontSize: 11, display: 'block', marginBottom: 2 }}>Threshold</label>
                          <input id={`rule-thresh-${rule.id}`} type="number" defaultValue={rule.threshold} style={inputS()} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div>
                          <label style={{ color: colors.textMuted, fontSize: 11, display: 'block', marginBottom: 2 }}>Severidade</label>
                          <select id={`rule-sev-${rule.id}`} defaultValue={rule.severity} style={inputS()}>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ color: colors.textMuted, fontSize: 11, display: 'block', marginBottom: 2 }}>Canais</label>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label style={{ color: colors.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="checkbox" id={`rule-ch-tg-${rule.id}`} defaultChecked={(rule.channels || []).includes('telegram')} /> Telegram
                            </label>
                            <label style={{ color: colors.textMuted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input type="checkbox" id={`rule-ch-email-${rule.id}`} defaultChecked={(rule.channels || []).includes('email')} /> Email
                            </label>
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" onClick={() => {
                        const channels: string[] = [];
                        if ((document.getElementById(`rule-ch-tg-${rule.id}`) as HTMLInputElement)?.checked) channels.push('telegram');
                        if ((document.getElementById(`rule-ch-email-${rule.id}`) as HTMLInputElement)?.checked) channels.push('email');
                        saveRule({
                          ...rule,
                          metric: (document.getElementById(`rule-metric-${rule.id}`) as HTMLSelectElement)?.value,
                          operator: (document.getElementById(`rule-op-${rule.id}`) as HTMLSelectElement)?.value,
                          threshold: parseFloat((document.getElementById(`rule-thresh-${rule.id}`) as HTMLInputElement)?.value || '0'),
                          severity: (document.getElementById(`rule-sev-${rule.id}`) as HTMLSelectElement)?.value,
                          channels,
                        });
                      }} style={{ ...btnS(colors.green), padding: '6px 16px' }}>Salvar Regra</Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* B1.4: Historico de incidentes timeline */}
      {alertSection.value === 'incidentes' && (
        <div>
          {incidents.value.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>
              Nenhum incidente registrado
            </div>
          )}
          <div style={{ position: 'relative', paddingLeft: 24 }}>
            <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: colors.cardBorder }} />
            {incidents.value.map((inc: any, i: number) => {
              const isFiring = inc.status === 'firing';
              const statusColor = isFiring ? colors.red : colors.green;
              return (
                <div key={inc.id || i} style={{ position: 'relative', marginBottom: 12, paddingLeft: 20 }}>
                  <div style={{
                    position: 'absolute', left: -4, top: 6, width: 12, height: 12,
                    borderRadius: '50%', background: statusColor, border: '2px solid #0d1117',
                  }} />
                  <div style={{ background: colors.card, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: statusColor, fontWeight: 700, fontSize: 12 }}>
                          {isFiring ? 'FIRING' : 'RESOLVED'}
                        </span>
                        <span style={{
                          padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                          background: `${getSeverityColor(inc.severity)}25`, color: getSeverityColor(inc.severity),
                        }}>{inc.severity}</span>
                      </div>
                      <span style={{ fontSize: 10, color: colors.textMuted }}>
                        {inc.timestamp ? new Date(inc.timestamp).toLocaleString('pt-BR') : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: colors.text, fontWeight: 600 }}>{inc.ruleName || inc.ruleId}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{inc.message}</div>
                    {inc.resolvedAt && (
                      <div style={{ fontSize: 11, color: colors.green, marginTop: 4 }}>
                        Resolvido em: {new Date(inc.resolvedAt).toLocaleString('pt-BR')}
                        {' '}(durou {Math.round((new Date(inc.resolvedAt).getTime() - new Date(inc.timestamp).getTime()) / 60000)}min)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" size="xs" onClick={fetchAlerts}>Atualizar</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function btnS(bg: string): Record<string, string | number> {
  return { padding: '4px 10px', background: bg, color: T.text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
}

function inputS(): Record<string, string | number> {
  return { width: '100%', padding: '6px 8px', background: T.bgHover, color: T.border, border: '1px solid #374151', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' as any };
}
